import type { SelectChangeEvent } from "@mui/material"
import { Box, Checkbox, FormControl, MenuItem, Select } from "@mui/material"
import { type FC, useMemo } from "react"
import type { ControllerRenderProps } from "react-hook-form"
import { Controller, useFormContext } from "react-hook-form"

import { useGetProductAislesPreferenceWarehouseShelvings } from "../../hooks"
import type { ProductAislesPreferenceFormValues } from "../../types"
import { getWarehouseShelvingAisleOptions, getWarehouseShelvingLocationOptions } from "../../utils"

import AutoComplete from "@/components/common/Autocomplete/AutoComplete"
import MultiSelect from "@/components/MultiSelect/MultiSelect"
import {
  DEFAULT_AUTOCOMPLETE_VALUE,
  FIELD_ERROR_CLASSES,
  FIELD_LABEL_CLASSES,
  SELECT_STYLING_PROPS,
} from "@/constants/forms"
import { WarehouseType } from "@/graphql/codegen/graphql"
import { useLookupWarehouses } from "@/hooks/useLookupWarehouses"
import type { AutocompleteOption } from "@/types/form-fields"
import { getAutoCompleteOptionLabel } from "@/utils/forms"

const ProductAislesPreferenceForm: FC = () => {
  const { watch, setValue, control } = useFormContext<ProductAislesPreferenceFormValues>()
  const selectedWarehouse = watch("warehouse")
  const selectedLocationCode = watch("location")
  const { warehouses, isFetching: isFetchingWarehouses } = useLookupWarehouses([WarehouseType.AkrochemManaged])
  const { warehouseShelvings, reset: resetWarehouseShelvings } = useGetProductAislesPreferenceWarehouseShelvings(
    selectedWarehouse.id
  )
  const locationOptions = useMemo(() => getWarehouseShelvingLocationOptions(warehouseShelvings), [warehouseShelvings])
  const aisleOptions = useMemo(
    () => getWarehouseShelvingAisleOptions(warehouseShelvings, selectedLocationCode),
    [warehouseShelvings, selectedLocationCode]
  )

  const handleWarehouseChange = () => {
    setValue("location", "")
    setValue("aisles", [])
    resetWarehouseShelvings()
  }

  const handleLocationChange = (
    e: SelectChangeEvent<string>,
    field: ControllerRenderProps<ProductAislesPreferenceFormValues, "location">
  ) => {
    field.onChange(e)
    setValue("aisles", [])
  }

  return (
    <>
      <AutoComplete<AutocompleteOption<string>>
        label='Warehouse'
        placeholder='Select One'
        name='warehouse'
        id='warehouse-autocomplete'
        options={warehouses}
        getOptionLabel={getAutoCompleteOptionLabel}
        control={control}
        clearOnEscape={true}
        defaultEmptyState={DEFAULT_AUTOCOMPLETE_VALUE}
        loading={isFetchingWarehouses}
        onChange={handleWarehouseChange}
        required
      />
      <Controller
        name='location'
        control={control}
        render={({ field, fieldState: { error } }) => (
          <FormControl fullWidth className='mt-6'>
            <label id='location-label' className={FIELD_LABEL_CLASSES}>
              Location<span className='text-red-600'>*</span>
            </label>
            <Select
              {...field}
              {...SELECT_STYLING_PROPS}
              labelId='location-label'
              error={!!error}
              disabled={!selectedWarehouse.id}
              renderValue={(selected) => selected || <span className='text-gray-500'>Please Select</span>}
              onChange={(e) => handleLocationChange(e, field)}
            >
              {locationOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {error?.message && (
              <p className={FIELD_ERROR_CLASSES} data-testid='location-code-error'>
                {error.message}
              </p>
            )}
          </FormControl>
        )}
      />
      <Controller
        name='aisles'
        control={control}
        render={({ field, fieldState: { error } }) => (
          <Box className='mt-6'>
            <label htmlFor='aisles-select' className={FIELD_LABEL_CLASSES}>
              Aisles<span className='text-red-600'>*</span>
            </label>
            <MultiSelect
              {...field}
              id='aisle-select'
              value={field.value}
              options={aisleOptions}
              error={!!error}
              disabled={!selectedLocationCode}
            >
              {aisleOptions.map(({ label, value }) => (
                <MenuItem key={value} value={value}>
                  <Checkbox checked={field.value.includes(value)} />
                  {label}
                </MenuItem>
              ))}
            </MultiSelect>

            {error?.message && (
              <p className={FIELD_ERROR_CLASSES} data-testid='aisle-codes-error'>
                {error.message}
              </p>
            )}
          </Box>
        )}
      />
    </>
  )
}

export default ProductAislesPreferenceForm
